import { Checkbox, DatePicker, Input, InputNumber, Mentions, Radio, Select, Space, Switch } from 'antd';
import { CrownOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const CURRENCY_OPTIONS = [
  { label: '$', value: '$' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'INR', value: 'INR' },
];

const PERIOD_OPTIONS = [
  { label: 'Hourly', value: 'Hourly' },
  { label: 'Daily', value: 'Daily' },
  { label: 'Weekly', value: 'Weekly' },
  { label: 'Monthly', value: 'Monthly' },
  { label: 'Yearly', value: 'Yearly' },
];

export function normalizeSelectOptions(options = []) {
  return options.map((option) => {
    if (!option || typeof option !== 'object') return option;

    const rawValue = [
      option.value,
      option.user_id,
      option.userId,
      option.USER_ID,
      option.legacyUserId,
      option.id,
      option._id,
    ].find((candidate) => (
      candidate !== undefined
      && candidate !== null
      && String(candidate).trim() !== ''
    ));
    const value = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();

    return { ...option, value };
  }).filter((option) => (
    option === undefined
    || option === null
    || typeof option !== 'object'
    || String(option.value ?? '').trim() !== ''
  ));
}

function InputField({ field, value, onChange }) {
  return (
    <Input
      value={value}
      onChange={onChange}
      placeholder={field.placeholder}
      maxLength={field.maxLength}
      prefix={<CrownOutlined />}
    />
  );
}

function TextareaField({ field, value, onChange }) {
  return (
    <TextArea
      value={value}
      onChange={onChange}
      rows={field.rows ?? 5}
      placeholder={field.placeholder}
    />
  );
}

function SelectField({ field, options, value, onChange }) {
  const normalizedOptions = normalizeSelectOptions(options);
  return (
    <Select
      value={value}
      onChange={onChange}
      options={normalizedOptions}
      placeholder={field.placeholder}
      className="dynamic-popup__field-select"
      prefix={<CrownOutlined />}
    />
  );
}

function MultiSelectField({ field, options, value, onChange }) {
  const normalizedOptions = normalizeSelectOptions(options);
  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      options={normalizedOptions}
      placeholder={field.placeholder}
      className="dynamic-popup__field-select"
      prefix={<CrownOutlined />}
    />
  );
}

function SearchSelectField({ field, options, onSearch, value, onChange }) {
  const normalizedOptions = normalizeSelectOptions(options);
  return (
    <Select
      showSearch
      filterOption={false}
      notFoundContent={null}
      value={value}
      onChange={onChange}
      options={normalizedOptions}
      placeholder={field.placeholder}
      onSearch={onSearch}
      className="dynamic-popup__field-select"
      prefix={<CrownOutlined />}
    />
  );
}

function MentionField({ field, mentionOpts, onMentionSearch, value, onChange }) {
  return (
    <Mentions
      value={value}
      onChange={onChange}
      rows={field.rows ?? 5}
      placeholder={field.placeholder}
      prefix="@"
      options={mentionOpts}
      onSearch={onMentionSearch}
      className="dynamic-popup__mention-textarea"
    />
  );
}

/**
 * Composite rate input: single amount or range, with currency and period selectors.
 * Receives `value`/`onChange` from Ant Design Form.Item (controlled component protocol).
 */
function CandidateRateField({ value = {}, onChange }) {
  const period = value.period ?? 'Hourly';
  const isRange = value.isRange ?? false;

  function update(patch) {
    onChange?.({ ...value, ...patch });
  }

  return (
    <div>
      <Space.Compact style={{ width: '100%' }}>
        {isRange ? (
          <>
            <InputNumber
              controls={false}
              placeholder="From"
              value={value.minAmount}
              onChange={(v) => update({ minAmount: v })}
              style={{ flex: 1 }}
            />
            <InputNumber
              controls={false}
              placeholder="To"
              value={value.maxAmount}
              onChange={(v) => update({ maxAmount: v })}
              style={{ flex: 1 }}
            />
          </>
        ) : (
          <InputNumber
            controls={false}
            placeholder="0"
            value={value.amount}
            onChange={(v) => update({ amount: v })}
            style={{ flex: 2 }}
          />
        )}
        <Select
          value={value.currency ?? undefined}
          options={CURRENCY_OPTIONS}
          onChange={(v) => update({ currency: v })}
          style={{ width: 80 }}
        />
        <Select
          value={period}
          options={PERIOD_OPTIONS}
          onChange={(v) => update({ period: v })}
          style={{ width: 110 }}
        />
      </Space.Compact>
      <Space size="small" style={{ marginTop: 8 }}>
        <Switch checked={isRange} onChange={(v) => update({ isRange: v })} />
        <span>Range</span>
      </Space>
    </div>
  );
}

function DatePickerField({ field, value, onChange }) {
  return (
    <DatePicker
      value={value}
      onChange={onChange}
      placeholder={field.placeholder ?? 'Select date'}
      style={{ width: '100%' }}
      format="YYYY-MM-DD"
    />
  );
}

function PriorityCheckboxField({ field, checked, onChange }) {
  return <Checkbox checked={checked} onChange={onChange}>{field.checkboxLabel}</Checkbox>;
}

function NumberField({ field, value, onChange }) {
  return (
    <InputNumber
      value={value}
      onChange={onChange}
      min={field.min}
      max={field.max}
      placeholder={field.placeholder}
      style={{ width: '100%' }}
    />
  );
}

function RadioGroupField({ options, value, onChange }) {
  return <Radio.Group value={value} onChange={onChange} options={options} />;
}

/**
 * Maps field `type` strings to their React components.
 *
 * Each component receives the standard field props injected by PopupFields:
 *   `{ field, options, onSearch, mentionOpts, onMentionSearch }`
 * plus `value`/`onChange` (or `checked`/`onChange` for priority-checkbox)
 * injected by Ant Design Form.Item.
 *
 * To add a new field type: add a component here and register it.
 */
export const FIELD_REGISTRY = {
  input: InputField,
  text: InputField,
  textarea: TextareaField,
  select: SelectField,
  'multi-select': MultiSelectField,
  'search-select': SearchSelectField,
  'mention-textarea': MentionField,
  'candidate-rate': CandidateRateField,
  'priority-checkbox': PriorityCheckboxField,
  'date-picker': DatePickerField,
  'number': NumberField,
  'radio': RadioGroupField,
};

/**
 * Returns extra Form.Item props needed for a given field type.
 * @param {string} fieldType
 * @returns {Object}
 */
export function getFieldFormItemProps(fieldType) {
  if (fieldType === 'priority-checkbox') return { valuePropName: 'checked' };
  return {};
}
